import { clearAppSnapshot } from "../../services/appAccessSnapshot";
import { SessionEndedError } from '../../services/sessionErrors';
import { CentralRecoveryContext, CentralReadyContext } from '../../context/CentralReadyContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faArrowUpRightFromSquare, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { cmsSettingsAccess } from '../../helpers/cmsSettingsAccess';
import styles from './centralAccounts.module.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthStatus } from '../../components/auth/AuthStatus';
import { suiteSignInState } from '../../helpers/suiteAuthFlow';
import { cmsReturnPath, cmsDestination } from '../../helpers/authNavigation';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import { centralEnabled, centralManager, centralSignIn, centralAccessToken, accountsPortal, accountsRequest, cmsSession, refreshCmsSession, type Workspace } from '../../services/centralAuth';
import { appEnv } from '../../config/env';

function selectedSite(): Workspace | undefined {
  const session = cmsSession();
  return session?.context.workspaces.find(x => x.businessId === localStorage.getItem(appEnv.storageCurrentSiteKey));
}
const message = (e: unknown) => e instanceof Error ? e.message : 'Unable to complete the request. Please retry.';
let callback: Promise<{ session: Awaited<ReturnType<typeof refreshCmsSession>>; returnTo: string }> | undefined;
export function CentralCallback() {
  const [error, setError] = useState('');
  const navigate = useNavigate();
  useEffect(() => {
    let active = true;
    callback ??= centralManager.signinRedirectCallback().then(async user => { clearAppSnapshot(); return ({
      session: await refreshCmsSession(), returnTo: cmsReturnPath((user.state as { returnTo?: string } | undefined)?.returnTo),
    }); });
    void callback.then(({ session, returnTo }) => {
      if (active) navigate(cmsDestination(session.context.workspaces, localStorage.getItem(appEnv.storageCurrentSiteKey), returnTo), { replace: true, state: { returnTo } });
    }).catch(e => { if (active) setError(message(e)); });
    return () => { active = false; };
  }, [navigate]);
  return <AuthStatus product="CMS" loading={!error} title={error ? 'Sign-in interrupted' : undefined}
    description={error ? 'Please try signing in again to continue.' : undefined}>
    {error && <><Alert variant="danger">{error}</Alert><a className="btn btn-primary" href="/signin">Try signing in again</a></>}
  </AuthStatus>;
}
export function CentralSignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = cmsReturnPath(location.state?.from);
  const [error, setError] = useState(() => suiteSignInState('cms').error);
  const [signedOut, setSignedOut] = useState(() => suiteSignInState('cms').signedOut);
  const started = useRef(false);
  const signIn = () => {
    setError(''); setSignedOut(false);
    return centralSignIn(returnTo).catch(e => setError(message(e)));
  };
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const interrupted = suiteSignInState('cms').error;
    if (interrupted) { setError(interrupted); return; }
    void centralManager.getUser().then(async user => {
      if (user) {
        const session = await refreshCmsSession();
        localStorage.removeItem('cms-explicit-signout');
        navigate(cmsDestination(session.context.workspaces, localStorage.getItem(appEnv.storageCurrentSiteKey), returnTo), { replace: true, state: { returnTo } });
      } else if (!signedOut) await signIn();
    }).catch(e => setError(message(e)));
  }, [navigate, returnTo, signedOut]);
  return <AuthStatus product="CMS" loading={!error && !signedOut} title={signedOut ? "You're signed out" : error ? 'Sign-in interrupted' : undefined}
    description={signedOut ? 'Sign in again when you are ready to use CMS.' : undefined}>
    {(error || signedOut) && <>{error && <Alert variant="danger">{error}</Alert>}<Button onClick={() => { setError(''); void signIn(); }}>{signedOut ? 'Sign in to CMS' : 'Try signing in again'}</Button></>}
  </AuthStatus>;
}
export function CentralGate({ children, shell = false }: { children: ReactNode; shell?: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const authRoute = ['/signin', '/auth/callback'].includes(location.pathname);
  const destination = useRef(location);
  destination.current = location;
  const [ready, setReady] = useState(!centralEnabled);
  const [hasUser, setHasUser] = useState(false);
  const [error, setError] = useState('');
  const [sessionEnded, setSessionEnded] = useState(false);
  const wasVerified = useRef(false);
  const redirecting = useRef(false);
  const retry = useRef<() => void>(() => {});
  useEffect(() => {
    if (!centralEnabled || authRoute) return;
    let active = true;
    redirecting.current = false;
    const recover = () => {
      if (!active) return;
      if (wasVerified.current) {
        setSessionEnded(true);
      } else if (!redirecting.current) {
        redirecting.current = true;
        setHasUser(false); setReady(false);
        const target = destination.current;
        navigateRef.current('/signin', { replace: true, state: { from: cmsReturnPath(target.pathname + target.search + target.hash) } });
      }
    };
    const load = async () => {
      setError('');
      try {
        const user = await centralManager.getUser();
        if (!active) return;
        if (!user) { recover(); return; }
        setHasUser(!!cmsSession());
        await refreshCmsSession();
        if (active && !redirecting.current) {
          wasVerified.current = true; setHasUser(true); setSessionEnded(false); setReady(true);
        }
      } catch (e) {
        if (!active) return;
        if (e instanceof SessionEndedError) recover();
        else setError(message(e));
      }
    };
    retry.current = () => { void load(); };
    void load();
    window.addEventListener('cms-session-ended', recover);
    return () => { active = false; window.removeEventListener('cms-session-ended', recover); };
  }, [authRoute]);
  if (authRoute || !centralEnabled) return <>{children}</>;
  const errorNotice = error && !sessionEnded && <main className="container py-5"><Alert variant="danger">Unable to verify CMS access. {error}</Alert><Button onClick={() => retry.current()}>Retry</Button></main>;
  // A failed cold start has no workspace to preserve: show one recovery screen.
  if (error && !wasVerified.current) return errorNotice;
  return <CentralReadyContext.Provider value={ready && !error && !sessionEnded}>
    <CentralRecoveryContext.Provider value={sessionEnded}>
      {errorNotice}
      {(ready || (shell && hasUser)) && <>
        {sessionEnded && <section className={styles.accessNotice} role="status">
          <h2>Sign in again to continue</h2>
          <p>Your session has ended. Your open work has been kept on this screen. Sign in in another tab, then check your sign-in to resume.</p>
          {error && <p>Unable to verify sign-in right now. Please retry.</p>}
          <a className="btn btn-primary" href="/signin" target="_blank" rel="noopener noreferrer">Sign in in another tab</a>{' '}
          <Button onClick={() => retry.current()}>Check sign-in</Button>
        </section>}
        <div className={styles.accessBoundary} {...(sessionEnded ? { inert: '' } : {})} aria-disabled={sessionEnded || undefined}>{children}</div>
      </>}
    </CentralRecoveryContext.Provider>
  </CentralReadyContext.Provider>;
}
export function CentralProfileLink({ path }: { path: string }) {
  return <Card body><h1>Manage your account</h1><p>Your sign-in and personal details are managed in Accounts.</p><a href={`${accountsPortal}${path}`}>Open Accounts</a></Card>;
}
type Member = { id: string; email: string; name: string; role: number; enabled: boolean; pending: boolean; bookingColour: string; rowVersion: string };
const roles = ['Owner', 'Administrator', 'Writer', 'Publisher'];
export function CentralMembers() {
  const site = selectedSite();
  const [members, setMembers] = useState<Member[]>([]); const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(''); const [name, setName] = useState(''); const [role, setRole] = useState(3);
  const [showInvitation, setShowInvitation] = useState(false); const [inviteError, setInviteError] = useState('');
  const load = async () => { if (site) setMembers(await accountsRequest<Member[]>(`/cms/resources/${site.resourceId}/members`)); };
  useEffect(() => { void load().catch(e => setError(message(e))); }, [site?.resourceId]);
  const run = async (action: () => Promise<unknown>) => { setBusy(true); setError(''); try { await action(); await load(); } catch (e) { setError(message(e)); } finally { setBusy(false); } };
  if (!site || site.role > 2) return <Alert variant="warning">Select a site you own or administer to manage its members.</Alert>;
  const update = (member: Member, changes: object) => accountsRequest(`/cms/resources/${site.resourceId}/members/${member.id}`, 'PUT', { ...member, remove: false, ...changes });
  const closeInvitation = () => { if (!busy) setShowInvitation(false); };
  return <>
    <h1>Site access — {site.name}</h1>
    <p>These roles apply to this site. Billing account permissions are managed separately.</p>
    {error && <Alert variant="danger">{error}</Alert>}
    <div className="d-flex justify-content-end mb-3">
      <Button onClick={() => { setInviteError(''); setShowInvitation(true); }} disabled={busy}>Invite a person</Button>
    </div>
    <Table responsive className="align-middle">
      <thead><tr><th>Person</th><th>Role</th><th>Status</th><th>Actions</th></tr></thead>
      <tbody>{members.map(member => <tr key={member.id}>
        <td>{member.name}<br />{member.email}</td>
        <td><Form.Select aria-label={`Role for ${member.email}`} value={member.role} disabled={busy || (site.role !== 1 && member.role === 1)} onChange={e => void run(() => update(member, { role: Number(e.target.value) }))}>{roles.map((label, i) => <option key={label} value={i + 1} disabled={i === 0 && site.role !== 1}>{label}</option>)}</Form.Select></td>
        <td>{member.pending ? 'Invitation pending' : member.enabled ? 'Active' : 'Disabled'}</td>
        <td><div className="d-flex flex-wrap gap-2 py-1">
          <Button size="sm" disabled={busy || member.pending} onClick={() => void run(() => update(member, { enabled: !member.enabled }))}>{member.enabled ? 'Disable' : 'Enable'}</Button>
          {site.role === 1 && <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => window.confirm(`Remove ${member.email} from this site?`) && void run(() => update(member, { remove: true }))}>Remove</Button>}
        </div></td>
      </tr>)}</tbody>
    </Table>
    <Modal show={showInvitation} onHide={closeInvitation} centered backdrop={busy ? 'static' : true} keyboard={!busy} aria-labelledby="invite-person-title">
      <Modal.Header closeButton={!busy}><Modal.Title id="invite-person-title">Invite a person</Modal.Title></Modal.Header>
      <Form onSubmit={async e => {
        e.preventDefault();
        setBusy(true); setInviteError('');
        try {
          await accountsRequest(`/cms/resources/${site.resourceId}/invitations`, 'POST', { email, name, role });
          setEmail(''); setName(''); setRole(3); setShowInvitation(false);
          try { await load(); } catch (e) { setError(message(e)); }
        } catch (e) { setInviteError(message(e)); }
        finally { setBusy(false); }
      }}>
        <Modal.Body>
          {inviteError && <Alert variant="danger">{inviteError}</Alert>}
          <Form.Group className="mb-3" controlId="invite-person-name">
            <Form.Label>Name</Form.Label>
            <Form.Control autoFocus required maxLength={200} value={name} disabled={busy} onChange={e => setName(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3" controlId="invite-person-email">
            <Form.Label>Email</Form.Label>
            <Form.Control required type="email" value={email} disabled={busy} onChange={e => setEmail(e.target.value)} />
          </Form.Group>
          <Form.Group controlId="invite-person-role">
            <Form.Label>Site role</Form.Label>
            <Form.Select value={role} disabled={busy} onChange={e => setRole(Number(e.target.value))}>{roles.map((label, i) => <option key={label} value={i + 1} disabled={i === 0 && site.role !== 1}>{label}</option>)}</Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeInvitation} disabled={busy}>Cancel</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send invitation'}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  </>;
}
export function CentralSiteSettings({ children }: { children: ReactNode }) {
  const { site, isSiteOwner } = cmsSettingsAccess();
  if (!site) return <Alert variant="warning">Select a site to manage its settings.</Alert>;
  if (!isSiteOwner) return <Alert variant="warning">Only a site Owner can view and edit Site Details. Contact your site owner to make changes.</Alert>;
  return <>{children}</>;
}
export function CentralSiteAccount() {
  const { site, canManageBilling } = cmsSettingsAccess();
  if (!site) return <Alert variant="warning">Select a site to view its billing account.</Alert>;
  if (!canManageBilling) return <Alert variant="warning">Account Details are available to billing account Owners and Billing Administrators. Contact your billing account holder for subscription changes.</Alert>;
  return <section className="app-list-section">
    <div className="app-list-section__header">
      <div className={styles.accountSummary}>
        <span className={styles.accountIcon} aria-hidden="true"><FontAwesomeIcon icon={faFileInvoiceDollar} /></span>
        <div>
          <h1 className="app-list-section__title">{site.name} account</h1>
          <p className="app-list-section__summary">Subscriptions, storage and billing are managed in SmoothBadger Accounts.</p>
        </div>
      </div>
    </div>
    <div className="app-list-section__body">
      {site.entitlement.hasAccess === false && <Alert variant="warning">This site's access is suspended. Open Accounts to review its subscription or contact the account owner.</Alert>}
      <p>Review this site's plan, included options and media storage allowance in its central billing account.</p>
      <div className={styles.accountActions}>
        <a className="btn btn-primary" href={`${accountsPortal}/subscriptions/${site.resourceId}?accountId=${encodeURIComponent(site.accountId)}`}>
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Manage subscription &amp; storage
        </a>
        <a className="btn btn-outline-secondary" href={`${accountsPortal}/cms`}><FontAwesomeIcon icon={faGlobe} /> Your CMS sites</a>
      </div>
      <p className="text-secondary mb-0">Billing permissions are separate from site roles. Site content and website settings remain in CMS.</p>
    </div>
  </section>;
}
export function CentralCreateSite() {
  const [name, setName] = useState(''); const [slug, setSlug] = useState(''); const [accountId, setAccountId] = useState('');
  const [accounts, setAccounts] = useState<{ id: string; name: string; currency: string; frequency: string }[]>([]);
  const [trial, setTrial] = useState<{ eligible: boolean; reason: string }>(); const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  // Preserve the operation through retries so a failed local setup never duplicates the central resource.
  const [operationId] = useState(() => { const old = sessionStorage.getItem('cms-create-operation'); const id = old || crypto.randomUUID(); sessionStorage.setItem('cms-create-operation', id); return id; });
  useEffect(() => { void accountsRequest<{id:string;name:string;currency:string;frequency:string}[]>('/accounts').then(items => { setAccounts(items); if (items.length === 1) setAccountId(items[0].id); }).catch(e => setError(message(e))); }, []);
  useEffect(() => { let active = true; setTrial(undefined); void accountsRequest<{eligible:boolean;reason:string}>(`/trials/cms${accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''}`).then(value => { if (active) { setTrial(value); setError(''); } }).catch(e => { if (active) setError(message(e)); }); return () => { active = false; }; }, [accountId]);
  return <Card body><h1>Create a CMS site</h1><p>Start a 30-day Standard trial with 1 GB of media storage. You will not be charged automatically. Adding domain mapping or increasing storage requires a paid subscription.</p>{error && <Alert variant="danger">{error}</Alert>}<Form onSubmit={async e => { e.preventDefault(); setBusy(true); setError(''); try {
    let site = cmsSession()?.context.workspaces.find(x => x.businessId === localStorage.getItem(appEnv.storageCurrentSiteKey) && x.canProvision);
    const eligibility = await accountsRequest<{eligible:boolean;reason:string}>(`/trials/cms${accountId ? `?accountId=${encodeURIComponent(accountId)}` : ''}`);
    const catalogue = await accountsRequest<{products:{id:string;code:string}[];plans:{id:string;productId:string;code:string}[];prices:{id:string;planId:string;isPublished:boolean;currency:string;frequency:string;effectiveFrom:string;version:number}[]}>('/catalogue');
    const product = catalogue.products.find(p => p.code.toLowerCase() === 'cms');
    const plan = catalogue.plans.find(p => p.productId === product?.id && p.code === 'standard');
    const billing = accounts.find(a => a.id === accountId);
    const price = catalogue.prices.filter(p => p.planId === plan?.id && p.isPublished && p.currency === (billing?.currency || 'GBP') && p.frequency === (billing?.frequency || 'Monthly') && Date.parse(p.effectiveFrom) <= Date.now()).sort((a,b) => Date.parse(b.effectiveFrom)-Date.parse(a.effectiveFrom) || b.version-a.version)[0];
    const resourceId = await accountsRequest<string>('/cms/workspaces', 'POST', { operationId, accountId: accountId || null, planId: plan?.id || '00000000-0000-0000-0000-000000000000', name, useTrial: eligibility.eligible, priceId: price?.id });
    const session = await refreshCmsSession(); site = session.context.workspaces.find(x => x.resourceId === resourceId);
    if (!site) throw new Error('The new site could not be verified. Please retry.');
    const token = await centralAccessToken();
    const response = await fetch(`${appEnv.apiUrl}/CentralAccounts/Provision`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'SELECTED-SITEID': site.businessId }, body: JSON.stringify({ siteId: site.businessId, subDomain: slug, defaultLanguage: 'en-gb' }) });
    if (!response.ok) { const body = await response.json(); throw new Error(body.Messages?.[0] || 'Unable to finish setting up this site. Retry with the same details.'); }
    localStorage.setItem(appEnv.storageCurrentSiteKey, site.businessId); sessionStorage.removeItem('cms-create-operation'); window.location.assign(site.entitlement.hasAccess ? '/content' : `${accountsPortal}/subscriptions/${site.resourceId}?accountId=${encodeURIComponent(site.accountId)}&setup=1`);
  } catch (e) { setError(message(e)); } finally { setBusy(false); } }}>
    <Form.Label>Site name</Form.Label><Form.Control required maxLength={200} value={name} onChange={e => setName(e.target.value)} />
    <Form.Label>Site subdomain</Form.Label><Form.Control required pattern="[a-z0-9]+(-[a-z0-9]+)*" maxLength={63} value={slug} onChange={e => setSlug(e.target.value)} />
    {trial && !trial.eligible && <p>{trial.reason} This site will remain unavailable until payment is confirmed.</p>}
    <Form.Label>Billing account</Form.Label><Form.Select value={accountId} onChange={e => setAccountId(e.target.value)}><option value="">{accounts.length ? 'Choose a billing account' : 'Create my first billing account'}</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}</Form.Select><p>A customer account can hold several sites, each with its own subscription, access and 1 GB of included media storage. The billing account holder can extend storage in Accounts.</p><Button type="submit" disabled={busy || !trial || (accounts.length > 0 && !accountId)}>{busy ? 'Creating…' : trial?.eligible === false ? 'Create site and continue to billing' : 'Start 30-day site trial'}</Button>
  </Form></Card>;
}
